import LegalShell from "../components/LegalShell";

export default function Privacy() {
    return (
        <LegalShell title="Privacy Policy" updated="25 Feb 2026">
            <p>
                Your privacy matters to us. This policy explains what we collect, how we use it,
                and the controls you have. It applies to Vowze, an AI wedding-look planning tool.
            </p>

            <h2>1. What we collect</h2>
            <ul>
                <li><strong>Photos you upload</strong> — the bride and groom primary photos plus
                    any additional reference photos.</li>
                <li><strong>Questionnaire data</strong> — bride/groom names, ceremony types,
                    venue &amp; style preferences, cultural preference, favourite &amp; avoid colours,
                    venue city.</li>
                <li><strong>Contact</strong> — the phone number you verify via OTP for report delivery.</li>
                <li><strong>Consent record</strong> — a timestamped audit of the two checkboxes
                    you tick at the free-report and paid-unlock gates.</li>
                <li><strong>Payment</strong> — Razorpay payment id + status. We never see or store
                    your card number or CVV; Razorpay handles that end-to-end.</li>
                <li><strong>Usage</strong> — basic logs (IP, timestamp, request path) for security
                    and rate limiting.</li>
            </ul>

            <h2>2. How we use it</h2>
            <ul>
                <li>To generate your Couple Colour Palette, editorial couple portraits and event
                    styling — this involves sending your photos to AI providers (see §4).</li>
                <li>To deliver your report to the phone number you verified.</li>
                <li>To improve the reliability of Vowze — e.g. diagnose failed unlocks — using
                    <em> aggregated, de-identified</em> signals.</li>
                <li>To meet legal, accounting or fraud-prevention obligations.</li>
            </ul>

            <h2>3. What we do NOT do</h2>
            <ul>
                <li>We do <strong>not</strong> sell your photos or personal data to advertisers.</li>
                <li>We do <strong>not</strong> use your photos to train third-party AI models beyond
                    the transient inference call needed to generate <em>your</em> report.</li>
                <li>We do <strong>not</strong> publish or share your report or photos with anyone
                    else without your explicit consent.</li>
            </ul>

            <h2>4. Third-party AI providers</h2>
            <ul>
                <li>Anthropic (Claude) — text generation.</li>
                <li>Google Gemini (Nano Banana Pro) — image generation.</li>
                <li>Razorpay — payment processing.</li>
            </ul>
            <p>
                Each provider processes data under its own privacy policy and, where applicable,
                zero-retention API terms. We pass only the minimum information needed to produce
                your report.
            </p>

            <h2>5. Storage &amp; retention</h2>
            <ul>
                <li>Photos and generated images are stored on Emergent Object Storage (encrypted at rest).</li>
                <li>Reports are retained for <strong>90 days</strong> after generation. After that
                    they may be automatically purged unless you have paid, in which case they are
                    retained for <strong>18 months</strong> so you can revisit them.</li>
                <li>You can request early deletion at any time — see §7.</li>
            </ul>

            <h2>6. Cookies</h2>
            <p>
                We use a small number of first-party cookies for essential functionality (session
                token, remembering your test-unlock token during dev). We do not use third-party
                advertising or tracking cookies.
            </p>

            <h2>7. Your controls</h2>
            <ul>
                <li><strong>Delete</strong> — email <a href="mailto:privacy@vowze.app">privacy@vowze.app</a>
                    with the phone number you used and we&apos;ll delete every associated report and
                    photo within 7 days.</li>
                <li><strong>Export</strong> — same address; we&apos;ll send you a copy of your
                    questionnaire, consent record, and generated report.</li>
                <li><strong>Correct</strong> — you can re-run the questionnaire any time with
                    updated photos and details.</li>
            </ul>

            <h2>8. Security</h2>
            <p>
                We use HTTPS end-to-end, encrypt uploaded photos at rest, and follow the principle
                of least privilege for internal access. No system is 100&nbsp;% impenetrable — if you
                believe your account has been compromised, please write to us immediately.
            </p>

            <h2>9. Children</h2>
            <p>
                Vowze is designed for adults planning their own wedding. We do not knowingly
                collect data from anyone under 18. If you believe a minor has uploaded photos,
                please email us and we will delete them promptly.
            </p>

            <h2>10. Changes</h2>
            <p>
                We may update this policy from time to time. Material changes will be surfaced on
                the Vowze homepage before they take effect.
            </p>

            <h2>11. Contact</h2>
            <p>
                Questions or requests? Write to <a href="mailto:privacy@vowze.app">privacy@vowze.app</a>.
            </p>
        </LegalShell>
    );
}
